import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { FeedList } from "@/components/FeedList";
import { MapPin, ArrowLeft, Send, Phone } from "lucide-react";

const COVER = "https://images.unsplash.com/photo-1584598788860-2695a3a6c874";

export default function MemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/members/${id}`).then((r) => setM(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen bg-bone pt-40 px-10 text-center">
      <p className="font-serif text-4xl">Member not found.</p>
      <Link to="/directory" className="font-mono text-xs uppercase tracking-widest text-olive mt-6 inline-block">Back to directory</Link>
    </div>
  );
  if (!m) return <div className="min-h-screen bg-bone" />;

  return (
    <div className="min-h-screen bg-bone pb-24">
      <div className="relative h-[42vh] overflow-hidden">
        <img src={m.avatar || COVER} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-bone via-bone/30 to-charcoal/40" />
      </div>
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 -mt-32 relative z-10">
        <Reveal>
          <Link to="/directory" className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-charcoal/60 hover:text-charcoal mb-8"><ArrowLeft size={14} /> Directory</Link>
          <div className="bg-bone border border-charcoal/10 p-10 md:p-14" data-testid="member-profile">
            <div className="flex items-center gap-6">
              {m.avatar ? (
                <img src={m.avatar} alt={m.name} className="w-24 h-24 object-cover" />
              ) : (
                <div className="w-24 h-24 bg-olive text-bone flex items-center justify-center font-serif text-4xl">{m.name?.[0]}</div>
              )}
              <div>
                <h1 className="font-serif font-light text-5xl tracking-tight">{m.name}</h1>
                {m.role === "admin" && <span className="font-mono text-xs uppercase tracking-widest text-gold">Ministry Leader</span>}
                <div className="mt-4">
                  <button onClick={() => navigate("/messages", { state: { to: { id: m.id, name: m.name, avatar: m.avatar } } })}
                    data-testid="profile-message-btn"
                    className="inline-flex items-center gap-2 bg-charcoal text-bone px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                    <Send size={14} strokeWidth={1.5} /> Send Message
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10 mt-12">
              <Info label="Focus" value={m.focus} />
              <Info label="Location" value={m.location} icon={<MapPin size={14} />} />
              <Info label="Telephone" value={m.phone} icon={<Phone size={14} />} />
            </div>

            {m.bio && (
              <div className="mt-12">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">About</span>
                <p className="font-serif text-2xl md:text-3xl font-light leading-snug mt-4 text-charcoal/90">{m.bio}</p>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10" data-testid="profile-timeline">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">Timeline</span>
              <span className="h-px flex-1 bg-charcoal/10" />
            </div>
            <FeedList wallOwnerId={m.id} wallOwnerName={m.name} />
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function Info({ label, value, icon }) {
  return (
    <div className="bg-bone p-6">
      <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{label}</span>
      <p className="font-sans text-charcoal/80 mt-2 flex items-center gap-2">{icon}{value || "—"}</p>
    </div>
  );
}
